/**
 * The data the node's environment adds to it (the coding environment's language and code): one
 * section per field its plugin declares, written as the field's kind says (a choice, a line, a
 * few lines, code), and the values it holds that the environment does not declare, to take out.
 */
import { For, Match, Show, Switch, createSignal, type JSX } from "solid-js";
import type { FieldDef } from "@protocol/FieldDef";
import type { Node } from "@protocol/Node";
import { useApp } from "../../app/context";
import { undeclared, valueOf, withValue } from "../../shared/fields";
import { toast } from "../../shared/toast";
import { ChoicePicker, copyText } from "../../shared/ui";

/** Every field of the node's environment, then the values it does not declare. */
export function NodeData(props: { node: () => Node }): JSX.Element {
  const { fields } = useApp();
  return (
    <>
      <For each={fields.list()}>{(field) => <FieldSection node={props.node} field={field} />}</For>
      <OtherData node={props.node} />
    </>
  );
}

/** Sets field `id` of the node to `value` ("" takes it out), in one edit. */
function useSetValue(node: () => Node) {
  const { actions } = useApp();
  return (id: string, value: string) => actions.updateNode(node().id, { data: { [id]: withValue(node().data, id, value)[id] ?? "" } });
}

/** One field, edited as its kind says. */
function FieldSection(props: { node: () => Node; field: FieldDef }): JSX.Element {
  const { fields } = useApp();
  const set = useSetValue(props.node);
  const f = () => props.field;
  const id = () => `node-field-${f().id}`;
  return (
    <section class="grp" data-field={f().id}>
      <h3 title={f().summary || undefined}>{f().label}</h3>
      <Switch>
        <Match when={f().kind === "choice"}>
          <ChoicePicker
            id={id()}
            label={f().label}
            value={valueOf(props.node(), f().id)}
            blank={`No ${f().label.toLowerCase()}`}
            choices={fields.choices(f())}
            onAdd={(value) => fields.add(f().id, value)}
            onChange={(value) => void set(f().id, value)}
          />
        </Match>
        <Match when={f().kind === "text"}>
          <input
            class="field"
            id={id()}
            maxlength="200"
            value={valueOf(props.node(), f().id)}
            onChange={(e) => void set(f().id, e.currentTarget.value.trim())}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          />
        </Match>
        <Match when={true}>
          <LongValue node={props.node} field={f()} />
        </Match>
      </Switch>
    </section>
  );
}

/** A field of several lines (`long`) or of code (`code`): shown with Copy / Edit, or a button to write one. */
function LongValue(props: { node: () => Node; field: FieldDef }): JSX.Element {
  const set = useSetValue(props.node);
  const [editing, setEditing] = createSignal(false);
  const value = () => valueOf(props.node(), props.field.id);
  const code = () => props.field.kind === "code";
  const name = () => props.field.label.toLowerCase();
  let area!: HTMLTextAreaElement;
  return (
    <Switch>
      <Match when={editing()}>
        <textarea
          class={code() ? "codeta" : "field"}
          id={`node-field-${props.field.id}`}
          ref={area}
          rows={code() ? 8 : 5}
          maxlength="4000"
          spellcheck={!code()}
          placeholder={code() ? "Paste a few lines of code" : `Write the ${name()}`}
          value={value()}
        />
        <div class="btnrow">
          <button
            type="button"
            class="btn primary"
            data-field-save={props.field.id}
            onClick={() =>
              void set(props.field.id, area.value).then((ok) => {
                if (ok) toast(area.value.trim() ? `${props.field.label} saved.` : `${props.field.label} removed.`);
                setEditing(false);
              })
            }
          >
            Save
          </button>
          <button type="button" class="btn" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      </Match>
      <Match when={value()}>
        <div class="codebox">
          <div class="codehead">
            <span>{props.field.label}</span>
            <span class="sp" />
            <button type="button" class="mini" onClick={() => void copyText(value()).then((ok) => toast(ok ? "Copied." : "Copy is blocked here. Select the text and copy it."))}>
              Copy
            </button>
            <button type="button" class="mini" data-field-edit={props.field.id} onClick={() => setEditing(true)}>
              Edit
            </button>
          </div>
          <Show when={code()} fallback={<p class="longvalue">{value()}</p>}>
            <pre>
              <code>{value()}</code>
            </pre>
          </Show>
        </div>
      </Match>
      <Match when={!value()}>
        <button type="button" class="btn" data-field-add={props.field.id} onClick={() => setEditing(true)}>
          {code() ? "Add a few lines of code" : `Write the ${name()}`}
        </button>
      </Match>
    </Switch>
  );
}

/** The values the node holds that its environment does not add (an older file's), each to take out. */
function OtherData(props: { node: () => Node }): JSX.Element {
  const { fields } = useApp();
  const set = useSetValue(props.node);
  const others = () => undeclared(props.node(), fields.list());
  return (
    <Show when={others().length}>
      <section class="grp" id="node-other-data">
        <h3 title="Values this environment does not use: kept with the node until taken out">Other data</h3>
        <ul class="other-data">
          <For each={others()}>
            {([key, value]) => (
              <li data-other-field={key}>
                <span class="mono small">{key}</span>
                <span class="muted small value">{value.length > 60 ? `${value.slice(0, 60)}…` : value}</span>
                <button type="button" class="mini" title={`Take ${key} out of the node`} onClick={() => void set(key, "")}>
                  ×
                </button>
              </li>
            )}
          </For>
        </ul>
      </section>
    </Show>
  );
}
